import { MenuItem } from 'primeng/api';
import { ClientDealsSummaryData } from '@/pages/deals/data-access/deals-api.service';
import { clientOpportunityTabOptions, ClientOpportunityCountKey } from '@/pages/deals/data-access/deals.option';
import { ExtendedMenuItem } from '../menu.types';
import { AI_FEATURE_ENABLED } from '@/shared/utilities/feature-flags';

export interface ClientOpportunityDealCounts {
    allDeals?: number;
    liveDeals: number;
    expressedInterestDeals: number;
    declinedDeals: number;
    closingDeals?: number;
    completedDeals?: number;
    missedDeals: number;
    withdrawnDeals?: number;
}

function buildClientOpportunitySubmenu(
    dealCounts: ClientOpportunityDealCounts | undefined,
    showBadges: boolean,
    translate: (labelKey: string) => string
): ExtendedMenuItem[] {
    return clientOpportunityTabOptions.map((tab) => {
        const count = dealCounts?.[tab.countKey as keyof ClientOpportunityDealCounts];
        const menuItem: ExtendedMenuItem = {
            label: translate(tab.labelKey),
            routerLink: [tab.route],
        };
        if (showBadges && count !== undefined && count !== null) {
            menuItem.badge = String(count);
        }
        return menuItem;
    });
}

function sumClientDealCounts(dealCounts: ClientOpportunityDealCounts): number {
    return (
        (dealCounts.liveDeals ?? 0) +
        (dealCounts.expressedInterestDeals ?? 0) +
        (dealCounts.closingDeals ?? 0) +
        (dealCounts.completedDeals ?? 0) +
        (dealCounts.declinedDeals ?? 0) +
        (dealCounts.missedDeals ?? 0) +
        (dealCounts.withdrawnDeals ?? 0)
    );
}

/** Map API summary counts to sidebar badge keys (tab order). */
export function mapClientDealsSummaryToOpportunityCounts(
    summary: ClientDealsSummaryData | null | undefined
): ClientOpportunityDealCounts {
    const counts: ClientOpportunityDealCounts = {
        liveDeals: summary?.live ?? 0,
        expressedInterestDeals: summary?.expressedInterest ?? 0,
        closingDeals: summary?.closing ?? 0,
        completedDeals: summary?.completed ?? 0,
        declinedDeals: summary?.declined ?? 0,
        missedDeals: summary?.missed ?? 0,
        withdrawnDeals: summary?.withdrawm ?? summary?.withdrawn ?? 0,
    };
    return {
        ...counts,
        allDeals: sumClientDealCounts(counts),
    };
}

/** Apply badge counts for every client opportunity tab route. */
export function forEachClientOpportunityTabRoute(
    counts: ClientOpportunityDealCounts,
    apply: (route: string, count: number) => void
): void {
    for (const tab of clientOpportunityTabOptions) {
        apply(tab.route, counts[tab.countKey as keyof ClientOpportunityDealCounts] ?? 0);
    }
}

function buildClientOpportunitiesParent(
    label: string,
    dealCounts: ClientOpportunityDealCounts | undefined,
    showBadges: boolean,
    translate: (labelKey: string) => string
): ExtendedMenuItem {
    return {
        label,
        menuType: 'submenu',
        iconPath: '/icons/deals.svg',
        items: buildClientOpportunitySubmenu(dealCounts, showBadges, translate),
    };
}

/**
 * Client sidebar items (same structure as admin RM menus).
 * @param translate - resolves ADMIN.DEALS_TAB.* keys (same labels as deals page tabs)
 */
export function getClientMenuItems(
    logoutCallback: () => void,
    dealCounts?: ClientOpportunityDealCounts,
    options?: { loading?: boolean; error?: boolean },
    translate: (labelKey: string) => string = (key) => key,
    askBettaAlphaCallback: () => void = () => {}
): ExtendedMenuItem[] {
    const countsWithAll: ClientOpportunityDealCounts | undefined = dealCounts
        ? {
              ...dealCounts,
              allDeals: dealCounts.allDeals ?? sumClientDealCounts(dealCounts),
          }
        : undefined;

    const opportunitiesLabel = options?.error
        ? 'Opportunities (Error)'
        : options?.loading
          ? 'Opportunities (Loading...)'
          : 'Opportunities';

    return [
        {
            label: 'Dashboard',
            iconPath: '/icons/dashboard.svg',
            routerLink: ['/client/dashboard'],
        } as ExtendedMenuItem,

        ...(AI_FEATURE_ENABLED ? [{
            label: 'Ask BettaAlpha',
            iconPath: '/icons/ai-sparkle.svg',
            command: askBettaAlphaCallback
        } as ExtendedMenuItem] : []),
        {
            label: 'My Portfolio',
            iconPath: '/icons/deals.svg',
            routerLink: ['/client/my-portfolio'],
        } as ExtendedMenuItem,
        buildClientOpportunitiesParent(
            opportunitiesLabel,
            countsWithAll,
            !!dealCounts && !options?.loading && !options?.error,
            translate
        ),
        {
            label: 'Tasks',
            iconPath: '/icons/book.svg',
            menuType: 'box-submenu',
            expanded: false,
            defaultCollapsed: true,
            boxSections: [
                {
                    title: 'Deals',
                    items: [
                        { label: 'Deal Subscription', routerLink: ['/client/new-tasks/deals/deal-subscription'] },
                        // Client: Deal Other hidden — client deal tasks use Deal Subscription only.
                        // { label: 'Deal Other', routerLink: ['/client/new-tasks/deals/deal-other'] },
                        { label: 'History', routerLink: ['/client/new-tasks/deals/history'] },
                    ],
                },
            ],
        } as ExtendedMenuItem,

        {
            label: 'Notifications',
            iconPath: '/icons/bell-outlined.svg',
            routerLink: ['/client/notifications'],
        } as ExtendedMenuItem,
        {
            label: 'Questions',
            iconPath: '/icons/question-answer-file.svg',
            routerLink: ['/questions'],
        } as ExtendedMenuItem,
        {
            label: 'Feedback',
            iconPath: '/icons/message.svg',
            routerLink: ['/feedback'],
        } as ExtendedMenuItem,
        {
            label: 'Learning Hub',
            iconPath: '/icons/book.svg',
            menuType: 'submenu',
            items: [{ label: 'Content', routerLink: ['/client/learning-hub'] }],
        } as ExtendedMenuItem,
        {
            label: 'My Profile',
            iconPath: '/icons/user.svg',
            routerLink: ['/user-profile'],
        } as ExtendedMenuItem,
        {
            label: 'Logout',
            iconPath: '/icons/log-out.svg',
            command: logoutCallback,
        } as ExtendedMenuItem,
    ];
}

/** @deprecated Use getClientMenuItems */
export function getClientLoadingMenu(logoutCallback: () => void): MenuItem {
    return { label: '', items: getClientMenuItems(logoutCallback, undefined, { loading: true }) };
}

/** @deprecated Use getClientMenuItems */
export function getClientMenuWithDeals(dealCounts: ClientOpportunityDealCounts, logoutCallback: () => void): MenuItem {
    return { label: '', items: getClientMenuItems(logoutCallback, dealCounts) };
}

/** @deprecated Use getClientMenuItems */
export function getClientErrorMenu(logoutCallback: () => void): MenuItem {
    return { label: 'Home', items: getClientMenuItems(logoutCallback, undefined, { error: true }) };
}
